import React, { useEffect, useMemo } from 'react';
import { DefaultValues, SubmitHandler, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Alert, Button, MenuItem, Stack, TextField } from '@mui/material';
import { StockMovementBase } from '../../api/dto';
import { stockMovementBaseSchema } from '../../utils/zodSchemas';
import { useInStock } from '../../api/queries';
import { fr } from '../../i18n/fr';
import { STOCK_STATE_LABEL } from '../../utils/mapping';
import ProductSelectorField from './fields/ProductSelectorField';
import { ProductSelectionItem } from '../../types';

interface Props {
  storeId: string;
  initialProductId?: string;
  initialProduct?: ProductSelectionItem | null;
  onSubmitted?: () => void;
}

const InForm: React.FC<Props> = ({ storeId, initialProductId, initialProduct, onSubmitted }) => {
  const mutation = useInStock();
  const defaultValues = useMemo<DefaultValues<StockMovementBase>>(
    () => ({
      store_id: storeId,
      stock_state: 'available' as StockMovementBase['stock_state'],
      quantity: 1,
      product_id: initialProductId ?? '',
      manual: { description: '' },
    }),
    [storeId, initialProductId]
  );
  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    setValue,
    control,
  } = useForm<StockMovementBase>({
    resolver: zodResolver(stockMovementBaseSchema),
    defaultValues,
  });

  useEffect(() => {
    setValue('store_id', storeId);
  }, [setValue, storeId]);

  useEffect(() => {
    setValue('product_id', initialProductId ?? '');
  }, [initialProductId, setValue]);

  const onSubmit: SubmitHandler<StockMovementBase> = ({ source_doc: _sourceDoc, ...values }) => {
    const payload: StockMovementBase = {
      ...values,
      product_id: values.product_id?.trim() ? values.product_id.trim() : null,
      manual: values.manual?.description?.trim()
        ? { description: values.manual.description.trim() }
        : undefined,
    };

    mutation.mutate(payload, {
      onSuccess: () => {
        reset({
          ...defaultValues,
          store_id: storeId,
          stock_state: values.stock_state,
          quantity: values.quantity,
        });
        onSubmitted?.();
      },
    });
  };

  return (
    <Stack
      component="form"
      onSubmit={handleSubmit(onSubmit)}
      spacing={2}
      aria-label="in-form"
    >
      <input type="hidden" {...register('store_id')} />
      {mutation.isError && (
        <Alert severity="error">{(mutation.error as Error).message}</Alert>
      )}
      <ProductSelectorField
        control={control}
        name="product_id"
        label={fr.product}
        error={!!errors.product_id}
        helperText={errors.product_id?.message}
        initialSelection={initialProduct ?? null}
      />
      <TextField
        select
        label={fr.stockState}
        {...register('stock_state')}
        fullWidth
      >
        {Object.entries(STOCK_STATE_LABEL).map(([value, label]) => (
          <MenuItem key={value} value={value}>
            {label}
          </MenuItem>
        ))}
      </TextField>
      <TextField
        type="number"
        label={fr.quantity}
        inputProps={{ min: 0, step: 1 }}
        {...register('quantity', { valueAsNumber: true })}
        error={!!errors.quantity}
        helperText={errors.quantity?.message}
        fullWidth
      />
      <TextField
        label={fr.manualDescription}
        {...register('manual.description')}
        multiline
        minRows={2}
        fullWidth
      />
      <Button
        type="submit"
        variant="contained"
        disabled={mutation.isPending}
      >
        {fr.saveMovement}
      </Button>
    </Stack>
  );
};

export default InForm;
